import React from 'react';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { TbInfoCircle } from 'react-icons/tb';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tectonics/components/ui/tooltip';
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@tectonics/components/ui/popover';
import { useBreakpoint } from '@tectonics/hooks/use-breakpoint';

interface FieldCardSecurityCodeTooltipProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly inputRef: RefObject<HTMLInputElement>;
}

function FieldCardSecurityCodeTooltip({ open, onOpenChange, inputRef }: FieldCardSecurityCodeTooltipProps): ReactNode {
  const { t } = useTranslation('opened-page', { keyPrefix: 'volcanes.form.cardSecurityCode' });
  const { breakpoint } = useBreakpoint();

  const icon = <TbInfoCircle className="size-4" />;
  const title = t('tooltipTitle');
  const description = t('tooltipDescription');

  const keepOpenOnInputFocus = (event: Event): void => {
    if (event.target === inputRef.current) event.preventDefault();
  };

  if (breakpoint === 'xs') {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger aria-label={title}>{icon}</PopoverTrigger>
        <PopoverContent
          side="top"
          className="flex-col items-start rounded-[var(--radius-base)] border-0 bg-blue-950 p-3 text-white shadow-none"
          onOpenAutoFocus={(event: Event) => event.preventDefault()}
          onFocusOutside={keepOpenOnInputFocus}
        >
          <p>{title}</p>
          <p>{description}</p>
          <PopoverArrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-blue-950 fill-blue-950" />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip open={open} onOpenChange={onOpenChange}>
      <TooltipTrigger aria-label={title}>{icon}</TooltipTrigger>
      <TooltipContent className="flex-col items-start">
        <p>{title}</p>
        <p>{description}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export { FieldCardSecurityCodeTooltip };
